import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, Input, input, OnInit, output, signal, ViewChild, viewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, Validators } from '@angular/forms';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';

import { SelectItem, SelectScrollEvent, Size, StringUtils } from '@bn/primitives';
import { BnToastrService } from '@bn/shared/toastr';

import { TruthySelectAnythingSelectedPipe } from '../../../standalone/selects/pipes/truthy-select-anything-selected.pipe';
import { FormcontrolErrorsComponent } from '../../formcontrol-errors/formcontrol-errors.component';
import { SelectAction } from '../models/select-action.model';
import { SecondarySelectedItemsPipe } from '../pipes/secondary-selected-items-label.pipe';
import { ClearButtonSelectTemplateComponent } from '../templates/clearbutton-select-template/clearbutton-select-template.component';
import { HeaderSelectTemplateComponent } from '../templates/header-select-template/header-select-template.component';
import { LabelSelectTemplateComponent } from '../templates/label-select-template/label-select-template.component';
import { MultiLabelSelectTemplateComponent } from '../templates/multi-label-select-template/multi-label-select-template.component';
import { OptgroupSelectTemplateComponent } from '../templates/opt-group-select-template/opt-group-select-template.component';
import { OptionSelectTemplateComponent } from '../templates/option-select-template/option-select-template.component';

type SelectValue = string | number | (string | number)[];

@Component({
  selector: 'bn-web-ui-prime-select',
  templateUrl: './prime-select.component.html',
  styleUrls: ['./prime-select.component.scss'],
  imports: [
    ClearButtonSelectTemplateComponent,
    FaIconComponent,
    FormcontrolErrorsComponent,
    FormsModule,
    HeaderSelectTemplateComponent,
    OptionSelectTemplateComponent,
    OptgroupSelectTemplateComponent,
    MultiLabelSelectTemplateComponent,
    LabelSelectTemplateComponent,
    NgClass,
    NgSelectModule,
    NgStyle,
    NgTemplateOutlet,
    TranslateModule,
    TranslatePipe,
    TruthySelectAnythingSelectedPipe,
    SecondarySelectedItemsPipe,
  ],
  providers: [BnToastrService],
})
export class PrimeSelectComponent implements ControlValueAccessor, OnInit {
  ngControl = inject(NgControl, { self: true });

  #toastr = inject(BnToastrService);

  readonly select = viewChild.required<NgSelectComponent>('primeSelect');

  @ViewChild('primeSelect') set selectEl(select: NgSelectComponent) {
    if (this.autofocus()) {
      select.focus();
    }
  }

  @Input()
  disabled = false;

  // TODO (later): Not all options are backed by template design, add to template if needed
  autofocus = input(false, { transform: (value?: boolean) => value ?? false });
  bindLabel = input('Name', { transform: (value?: string) => value ?? 'Name' });
  bindValue = input('Id', { transform: (value?: string) => value ?? 'Id' });
  borderRadius = input(['md', 'md', 'md', 'md'], { transform: (value?: (Extract<Size, 'sm' | 'md' | 'lg'> | 'none')[]) => value ?? ['md', 'md', 'md', 'md'] });
  clearable = input(true, { transform: (value?: boolean) => value ?? true });
  clearTemplate = input('textClearButton', { transform: (value?: 'clearButton' | 'textClearButton') => value ?? 'textClearButton' });
  closeOnSelect = input(true, { transform: (value?: boolean) => value ?? true });
  disableInternalSearch = input(false, { transform: (value?: boolean) => value ?? false });
  dropdownSearchFilter = '';
  dropdownWidth = input('md', {
    transform: (value?: Extract<Size, '100' | 'sm' | 'md' | 'lg' | 'xl' | '150px' | '200px' | '250px' | '300px'>) => value ?? 'md',
  });
  dropdownWidthMobile = input<'default' | 'full' | undefined>('default');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  findAllSearchFn = input<(term: string, item: any) => boolean | undefined>();
  flagSize = input('md', { transform: (value?: 'xs' | 'sm' | 'md' | 'lg' | null) => value ?? 'md' });
  groupBy = input('', { transform: (value?: string) => value ?? '' });
  isGroupUpperCase = input(false, { transform: (value?: boolean) => value ?? false });
  headerText = input<string | undefined>();
  hideArrow = input<boolean | undefined>(true);
  hideClear = input(false, { transform: (value?: boolean) => value ?? false });
  hideMultipleSelectedItems = input(false, { transform: (value?: boolean) => value ?? false });
  hideName = input(false, { transform: (value?: boolean) => value ?? false });
  hideRequiredLabel = input(false, { transform: (value?: boolean) => value ?? false });
  hideSelected = input(false, { transform: (value?: boolean) => value ?? false });
  id = input<string | undefined>('');
  innerLabel = input<string | undefined>('');
  inputName = input<string | undefined>(undefined);
  isExternalSearch = input(false, { transform: (value?: boolean) => value ?? false });
  isLoading = input(false, { transform: (value?: boolean) => value ?? false });
  isMinSize = input<boolean | undefined>(false);
  isOpened = signal<boolean>(false);
  isRequired = false;
  isTouched = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items = input(<SelectItem[]>[], { transform: (value?: SelectItem[] | any[] | null | undefined) => value ?? <SelectItem[]>[] });
  itemStyle = input<'primary' | 'secondary' | undefined>('primary');
  label = input<string | undefined>(undefined);
  labelForId = input<string | undefined>(undefined);
  leftIcon = input('', { transform: (value?: IconProp) => value || '' });
  leftIconCircle = input(false, { transform: (value?: boolean) => value ?? false });
  leftIconClass = input('', { transform: (value?: string) => value ?? '' });
  maxSelectedItems = input(1000, { transform: (value?: number) => value || 1000 });
  minWidth = input<string | undefined>(undefined);
  placeholder = input<string>('');
  readonly = input(false, { transform: (value?: boolean) => value ?? false });
  required = input(false, { transform: (value?: boolean) => value ?? false });
  searchable = input(false, { transform: (value?: boolean) => value ?? false });
  searchPlaceholder = input('', { transform: (value?: string) => value || '' });
  selectableGroup = input(false, { transform: (value?: boolean) => value ?? false });
  selectedItems = signal<(string | number)[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedItemsLabels = signal<{ [key: string | number]: { label: string; country?: any } }>({});
  showEndSelectedIcon = input(false, { transform: (value?: boolean) => value ?? false });
  isMultipleSelect = input(false, { transform: (value?: boolean) => value ?? false });
  size = input('sm', { transform: (value?: 'auto' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | null) => value ?? 'sm' });
  virtualScroll = input(false, { transform: (value?: boolean) => value ?? false });
  width = input<string | undefined>(undefined);

  // Note: Works only with itemStyle="secondary"
  showFirstNPlusMore = input(0, { transform: (value?: number) => value || 0 });
  showOnlyCount = input(false, { transform: (value?: boolean) => value ?? false });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly defaultFindAllSearchFn: (term: string, item: any) => boolean = (_term, _item) => true;

  displayName = computed(() => {
    return this.inputName() || this.label() || 'Select';
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filteredItems: SelectItem[] | any[] = [];

  readonly atAdd = output<SelectItem>();

  readonly atRemove = output<SelectItem>();

  readonly clickAction = output<SelectAction>();

  readonly fetchMore = output<void>();

  // Just passing scroll from ng-select, making better type for it
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly scroll = output<SelectScrollEvent>();

  readonly search = output<string>();

  onChange: (value: SelectValue) => void = () => ({});
  onTouched: () => void = () => ({});

  constructor() {
    const ngControl = this.ngControl;

    if (ngControl) {
      ngControl.valueAccessor = this;
    }

    effect(() => {
      this.filteredItems = this.items();
    });

    // Get nice display labels for every selected id `{ 1234: 'Best pullzone I have', 2345: 'Second Best pullzone I have' }`
    effect(() => {
      const _bindValue = this.bindValue();
      const _bindLabel = this.bindLabel();
      // convert to array for single selections
      let _selectedItems = this.selectedItems();

      if (_selectedItems === null || _selectedItems === undefined) {
        _selectedItems = [];
      }

      if (!Array.isArray(_selectedItems)) {
        _selectedItems = [_selectedItems];
      }
      const labels = Object.fromEntries(
        _selectedItems.map((id) => {
          const fullItem = this.items().find((item) => item?.[_bindValue] === id);
          return [fullItem?.[_bindValue] ?? id, { label: fullItem?.[_bindLabel] ?? id, country: fullItem?.country }];
        }),
      );
      this.selectedItemsLabels.set(labels);
    });
  }

  ngOnInit(): void {
    this.isRequired = this.ngControl.control?.hasValidator(Validators.required) ?? false;
  }

  onClearAll(event?: Event | MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();

    this.select().close();
    this.select().clearModel();
    this.onChange(this.selectedItems());
    this.onTouched();
  }

  onClearItem(item: SelectItem): void {
    this.selectedItems.set(this.selectedItems().filter((val) => val !== item[this.bindValue()]));
    this.onChange(this.selectedItems());
  }

  onModelChange(value: string | number | (string | number)[]): void {
    this.onChange(value);

    if (!this.isTouched) this.onTouched();
  }

  writeValue(items: string[] | number[] | null): void {
    // select clear becomes null value
    if (!items) {
      this.selectedItems.set([]);
      return;
    }

    this.selectedItems.set(items);
  }

  registerOnChange(fn: (value: SelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleAdd(event: Event): void {
    this.atAdd.emit(event as unknown as SelectItem);
  }

  handleRemove(event: Event): void {
    this.atRemove.emit(event as unknown as SelectItem);
  }

  onBlur(event: FocusEvent): void {
    this.onSelectAction(<SelectAction>{
      action: event.type,
      value: this.selectedItems(),
    });
  }

  onSelectAction(event: SelectAction): void {
    switch (event.action) {
      case 'blur':
      case 'close':
        this.clickAction.emit(event);
        break;
      default:
        this.#toastr.danger(`${event.action} not yet implemented`, this.inputName());
        break;
    }
  }

  onClose(): void {
    this.isOpened.set(false);
    this.dropdownSearchFilter = '';
    this.refreshFilteredItems();

    this.select().close();
    this.clickAction.emit({ action: 'close' });
  }

  onOpen(): void {
    this.isOpened.set(true);

    if (this.virtualScroll() && this.searchable()) {
      this.search.emit('');
    }
  }

  onSearch({ term }: { term: string }): void {
    this.dropdownSearchFilter = term;

    if (this.virtualScroll()) {
      if (this.isExternalSearch()) this.filteredItems = []; // reset items if search changes

      this.search.emit(term);
    }

    this.refreshFilteredItems();
  }

  onSearchFilterChange(): void {
    this.refreshFilteredItems();

    this.onSelectAction(<SelectAction>{
      action: 'close',
      value: this.selectedItems(),
    });
  }

  onScrollToEnd(): void {
    // TODO: The 'emit' function requires a mandatory void argument
    this.fetchMore.emit();
  }

  onScroll(event: SelectScrollEvent): void {
    this.scroll.emit(event);
  }

  public open(): void {
    this.select()?.focus();
    this.select()?.open();
  }

  private refreshFilteredItems(): void {
    const items = this.items();
    const labelKey = this.bindLabel();

    if (Array.isArray(items)) {
      const sanitizedItems = items.map((item) => ({
        ...item,
        [labelKey]: StringUtils.htmlEncode(item[labelKey]).toString(),
      }));

      this.filteredItems = sanitizedItems;

      // skip client-side filtering when using external search (API handles filtering)
      if (this.isExternalSearch()) {
        return;
      }

      if (this.dropdownSearchFilter || this.disableInternalSearch() === false) {
        this.filteredItems = sanitizedItems.filter((item) => item[labelKey].toLowerCase().includes(this.dropdownSearchFilter.toLowerCase()));
      }
    } else {
      this.filteredItems = [];
    }
  }

  public onBadgeRemoveClick(id: SelectItem['Id'], event: MouseEvent): void {
    this.isOpened.set(false);
    event.stopPropagation(); // TODO: This doesn't work
    event.preventDefault(); // TODO: This doesn't work
    this.selectedItems.set(this.selectedItems().filter((val) => val !== id));
    this.onModelChange(this.selectedItems());
    this.isOpened.set(true);
  }
}
